/**
 * What this studio is called, when its week starts, and what time it is here.
 *
 * Fetched once, inside the protected branch of the route tree for the same
 * reason `AppDataProvider` is: a provider above the guard fires a read at a
 * server that is about to answer 401.
 *
 * It renders its children immediately rather than holding the app on a spinner
 * for one small read. Until the answer lands, the defaults below apply — and
 * they are the same values the migration inserts, so the only screen that can
 * flicker is one where somebody has changed them.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../lib/api'
import { setStudioZone } from '../lib/format'
import type { WeekStart } from '../lib/monthGrid'

export interface Workspace {
  name: string
  weekStartsOn: WeekStart
  /** An IANA name. Read-only here — it is a property of the deployment. */
  timezone: string
  /** Where the timezone came from, so the settings screen can say so. */
  timezoneSource: string
  /** ISO 4217, what the studio's own figures are in. */
  currency: string
  /** 1–12, the month the financial year opens. */
  fiscalYearStarts: number
  /** The categories a tag box offers first. */
  taskCategories: string[]
}

type Changes = {
  name?: string
  weekStartsOn?: WeekStart
  currency?: string
  fiscalYearStarts?: number
  taskCategories?: string[]
}

interface Wire {
  name: string
  week_starts_on: number
  timezone: string
  timezone_source: string
  currency?: string
  fiscal_year_starts?: number
  task_categories?: string[]
}

/** The same values migration 0012 inserts, so a slow read shows the truth. */
const FALLBACK: Workspace = {
  name: 'Studio Happens',
  weekStartsOn: 1,
  timezone: 'Asia/Kolkata',
  timezoneSource: 'STUDIO_TIMEZONE',
  currency: 'INR',
  fiscalYearStarts: 4,
  taskCategories: []
}

interface WorkspaceValue extends Workspace {
  /** False until the first read lands or fails. */
  isLoaded: boolean
  /** Push a change to the server and keep what comes back. Partners only. */
  save: (changes: Changes) => Promise<void>
}

const Ctx = createContext<WorkspaceValue | null>(null)

function fromWire(wire: Wire): Workspace {
  return {
    name: wire.name,
    // Anything other than the two is the server having changed under us; the
    // grid has columns for Sunday and Monday and for nothing else.
    weekStartsOn: wire.week_starts_on === 0 ? 0 : 1,
    timezone: wire.timezone,
    timezoneSource: wire.timezone_source,
    currency: wire.currency ?? 'INR',
    fiscalYearStarts: wire.fiscal_year_starts ?? 4,
    taskCategories: wire.task_categories ?? []
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspace, setWorkspace] = useState<Workspace>(FALLBACK)
  const [isLoaded, setIsLoaded] = useState(false)

  const apply = useCallback((next: Workspace) => {
    setWorkspace(next)
    // The one piece of this that has to reach code outside React: `today()`
    // is a plain function called from screens and from helpers alike.
    setStudioZone(next.timezone)
  }, [])

  useEffect(() => {
    let live = true
    void api
      .get<Wire>('/workspace')
      .then((wire) => {
        if (live) apply(fromWire(wire))
      })
      .catch(() => {
        // The defaults stand. A studio name that failed to load is not a
        // reason to put an error in front of somebody trying to work.
      })
      .finally(() => {
        if (live) setIsLoaded(true)
      })
    return () => {
      live = false
    }
  }, [apply])

  const save = useCallback(
    async (changes: Changes) => {
      const saved = await api.patch<Wire>('/workspace', {
        ...(changes.name === undefined ? {} : { name: changes.name }),
        ...(changes.weekStartsOn === undefined ? {} : { week_starts_on: changes.weekStartsOn }),
        ...(changes.currency === undefined ? {} : { currency: changes.currency }),
        ...(changes.fiscalYearStarts === undefined ? {} : { fiscal_year_starts: changes.fiscalYearStarts }),
        ...(changes.taskCategories === undefined ? {} : { task_categories: changes.taskCategories })
      })
      // What comes back, not what was sent. The server trims the name, so the
      // optimistic version would show trailing spaces the database does not
      // have. The zone is untouched by this route and so is left alone.
      setWorkspace((prev) => ({
        ...prev,
        name: saved.name,
        weekStartsOn: saved.week_starts_on === 0 ? 0 : 1,
        currency: saved.currency ?? prev.currency,
        fiscalYearStarts: saved.fiscal_year_starts ?? prev.fiscalYearStarts,
        taskCategories: saved.task_categories ?? prev.taskCategories
      }))
    },
    []
  )

  return <Ctx.Provider value={{ ...workspace, isLoaded, save }}>{children}</Ctx.Provider>
}

export function useWorkspace(): WorkspaceValue {
  const value = useContext(Ctx)
  if (value === null) {
    throw new Error('useWorkspace outside WorkspaceProvider. It wraps the signed-in shell.')
  }
  return value
}
